import { Box, Tab, Tabs } from '@mui/material';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import {
  JobAssignStatus,
  JobQueueTableType,
} from 'pages/iPriseV2/common/enum/TeamUserRoles';
import CommonJobQueueTable from 'pages/iPriseV2/common/JobQueueTable';
import React, { useCallback } from 'react';

import ColumnsSidebar from './ColumnsSidebar';

const JobQueueTable: React.FC = () => {
  const { businessHeadJobQueueService } = useStore();
  const {
    jobSubTab,
    unAllocatedSubTab,
    assignedSubTab,
    unAllocatedPage,
    unAllocatedRowsPerPage,
    assignedPage,
    assignedRowsPerPage,
    readyForReviewPage,
    readyForReviewRowsPerPage,
    readyForApprovedPage,
    readyForApprovedRowsPerPage,
    submittedPage,
    submittedRowsPerPage,
    selectedTeam,
    selectedSort,
    searchTerm,
    percentage,
  } = businessHeadJobQueueService;

  const handleJobSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      businessHeadJobQueueService.setJobSubTab(newValue);
    },
    [businessHeadJobQueueService],
  );

  const handleUnAllocatedSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      businessHeadJobQueueService.setUnAllocatedSubTab(newValue);
    },
    [businessHeadJobQueueService],
  );

  const handleAssignedSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      businessHeadJobQueueService.setAssignedSubTab(newValue);
    },
    [businessHeadJobQueueService],
  );

  return (
    <Box className="table-container-with-sidebar">
      <Box className="table-main-content">
        <Box className="sub-tabs-container">
          <Box className="sub-tabs-wrapper">
            <Tabs
              value={jobSubTab}
              onChange={handleJobSubTabChange}
              className="sub-tabs"
              variant="scrollable"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                className: 'sub-tab-indicator',
              }}>
              <Tab
                label={
                  <Box className="tab-label-with-badge">
                    <span className="tab-dot" />
                    Unallocated
                  </Box>
                }
                className="sub-tab"
              />
              <Tab label="Assigned" className="sub-tab" />
              <Tab label="Ready for Review" className="sub-tab" />
              <Tab label="Approved" className="sub-tab" />
              <Tab label="Submitted" className="sub-tab" />
            </Tabs>
          </Box>
        </Box>

        {jobSubTab === 0 && (
          <Box className="sub-tabs-container">
            <Box className="sub-tabs-wrapper">
              <Tabs
                value={unAllocatedSubTab}
                onChange={handleUnAllocatedSubTabChange}
                className="sub-tabs"
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  className: 'sub-tab-indicator',
                }}>
                <Tab label="TL Unallocated" className="sub-tab" />
                <Tab label="Recruiter Unallocated" className="sub-tab" />
              </Tabs>
            </Box>
          </Box>
        )}

        {jobSubTab === 0 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.BUSINESS_HEAD_UNALLOCATED}
            status={
              unAllocatedSubTab === 0
                ? JobAssignStatus.TL_UNALLOCATED
                : JobAssignStatus.RECRUITER_UNALLOCATED
            }
            queryKey="bh-job-postings-unallocated"
            page={unAllocatedPage}
            rowsPerPage={unAllocatedRowsPerPage}
            onPageChange={(p) =>
              businessHeadJobQueueService.setUnAllocatedPage(p)
            }
            onRowsPerPageChange={(r) =>
              businessHeadJobQueueService.setUnAllocatedRowsPerPage(r)
            }
            popperId="skills-popper-bh-unallocated"
            selectedSort={selectedSort}
            selectedTeam={selectedTeam}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 1 && (
          <Box className="sub-tabs-container">
            <Box className="sub-tabs-wrapper">
              <Tabs
                value={assignedSubTab}
                onChange={handleAssignedSubTabChange}
                className="sub-tabs"
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  className: 'sub-tab-indicator',
                }}>
                <Tab label="TL Assigned" className="sub-tab" />
                <Tab label="Recruiter Assigned" className="sub-tab" />
              </Tabs>
            </Box>
          </Box>
        )}

        {jobSubTab === 1 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.BUSINESS_HEAD_ASSIGNED}
            status={
              assignedSubTab === 0
                ? JobAssignStatus.TL_ASSIGNED
                : `${JobAssignStatus.RECRUITER_ASSIGNED},${JobAssignStatus.RECRUITER_ACCEPTED}`
            }
            queryKey="bh-job-postings-assigned"
            page={assignedPage}
            rowsPerPage={assignedRowsPerPage}
            onPageChange={(p) => businessHeadJobQueueService.setAssignedPage(p)}
            onRowsPerPageChange={(r) =>
              businessHeadJobQueueService.setAssignedRowsPerPage(r)
            }
            popperId="skills-popper-bh-assigned"
            selectedSort={selectedSort}
            selectedTeam={selectedTeam}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 2 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.BUSINESS_HEAD_READY_FOR_REVIEW}
            status={JobAssignStatus.READY_FOR_REVIEW}
            queryKey="bh-job-postings-ready-for-review"
            page={readyForReviewPage}
            rowsPerPage={readyForReviewRowsPerPage}
            onPageChange={(p) =>
              businessHeadJobQueueService.setReadyForReviewPage(p)
            }
            onRowsPerPageChange={(r) =>
              businessHeadJobQueueService.setReadyForReviewRowsPerPage(r)
            }
            popperId="skills-popper-bh-ready-for-review"
            selectedSort={selectedSort}
            selectedTeam={selectedTeam}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 3 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.BUSINESS_HEAD_APPROVED}
            status={JobAssignStatus.READY_FOR_REVIEW}
            queryKey="bh-job-postings-ready-for-approved"
            page={readyForApprovedPage}
            rowsPerPage={readyForApprovedRowsPerPage}
            onPageChange={(p) =>
              businessHeadJobQueueService.setReadyForApprovedPage(p)
            }
            onRowsPerPageChange={(r) =>
              businessHeadJobQueueService.setReadyForApprovedRowsPerPage(r)
            }
            popperId="skills-popper-bh-ready-for-approved"
            selectedSort={selectedSort}
            selectedTeam={selectedTeam}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 4 && (
          <CommonJobQueueTable
            tableType={JobQueueTableType.BUSINESS_HEAD_SUBMITTED}
            status={JobAssignStatus.SUBMITTED}
            queryKey="bh-job-postings-submitted"
            page={submittedPage}
            rowsPerPage={submittedRowsPerPage}
            onPageChange={(p) =>
              businessHeadJobQueueService.setSubmittedPage(p)
            }
            onRowsPerPageChange={(r) =>
              businessHeadJobQueueService.setSubmittedRowsPerPage(r)
            }
            popperId="skills-popper-bh-submitted"
            selectedSort={selectedSort}
            selectedTeam={selectedTeam}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
      </Box>
      <ColumnsSidebar />
    </Box>
  );
};

export default observer(JobQueueTable);
